import _ from 'underscore';
import React, { useCallback, useMemo } from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { getDisplayName } from '@bingads-webui-react/hoc-utils';

import { odata } from '@bingads-webui/http-util';
import legacyOdata from '@bingads-webui-component-legacy/http/channel/odata';
import { ImageOData } from '@bingads-webui-campaign/image-odata';
import { AzureUploader } from '@bingads-webui/azure-upload';
import { fromLegacyTimeZone, getUTCOffsetWithoutDST } from '@bingads-webui-universal/time-zone-converter';
import { translateLegacyToIANATimezone } from '@bingads-webui-universal/temporal-utitlities';
import { getODataErrors as getODataErrorsImpl } from '@bingads-webui-campaign/api-errors';

import {
  getDefaultMetricDateRange,
  AssetWithFoldersDataServiceProvider,
  withAdsStudioServicesConsumer
} from '@bingads-webui-campaign-react/ads-studio-app-shared-components';

import { AdsStudioAppNameODataHeader } from '../constants';
import { getLoggerWithoutPageInfo, getLogVersion } from './logger/custom-logger';

import { useAdsStudioUserPreference, useAdsStudioAccountPreference } from './preferences';

import {
  getFolderODataService,
  getImageBulkEditODataService,
  getAIGCODataService,
  getVideoODataService,
} from './odata-service';
import {
  getRepository,
  getFolderRepository,
  getAIGCRepository,
  getVideoRepository,
} from './repository';

export const prepareODataConfig = (accountTimeZone, oDataConfig) => {
  const tz = fromLegacyTimeZone(accountTimeZone);
  const timezoneUTCOffset = getUTCOffsetWithoutDST(tz);
  const ianaTimeZone = translateLegacyToIANATimezone(accountTimeZone);

  const oDataConfigObj = {
    odataToken: oDataConfig.odataToken || oDataConfig.oDataToken,
    oDataToken: oDataConfig.odataToken || oDataConfig.oDataToken,
    odataPath: oDataConfig.odataPath || oDataConfig.oDataPath,
    oDataPath: oDataConfig.odataPath || oDataConfig.oDataPath,
    odataTokenType: oDataConfig.oDataTokenType || oDataConfig.odataTokenType,
    oDataTokenType: oDataConfig.oDataTokenType || oDataConfig.odataTokenType,
    applicationName: AdsStudioAppNameODataHeader.value,
    beforeSend: xhr => ({
      onBeforeSendDone: () => {
        xhr.setRequestHeader(AdsStudioAppNameODataHeader.key, AdsStudioAppNameODataHeader.value);
      },
    }),
  };

  return {
    ianaTimeZone,
    timezoneUTCOffset,
    oDataConfig: oDataConfigObj,
  };
};

export function withAdsStudioServicesProvider(WrappedComponent, { loggerTag } = {}) {
  const Wrapper = (props) => {
    const {
      aid,
      cid,
      accountTimeZone,
      oDataConfig,
      preferenceService,
      activity,
      i18n,
      allowedAssetType,
      permissions,
      perfMetricDateRange,
      dateFormat,
    } = props;

    const {
      timezoneUTCOffset,
      ianaTimeZone,
      oDataConfig: oDataConfigObj,
    } = useMemo(() => prepareODataConfig(accountTimeZone, oDataConfig), [accountTimeZone, oDataConfig]);

    // get preference service
    const adsStudioUserPreferences = useAdsStudioUserPreference({ preferenceService });
    const adsStudioAccountPreferences = useAdsStudioAccountPreference({ preferenceService });

    const versionForLog = useMemo(() => (getLogVersion(permissions)), [permissions]);
    const logger = useMemo(() => getLoggerWithoutPageInfo(activity, versionForLog, loggerTag), [activity, versionForLog]);

    const imageODataService = useMemo(
      () => new ImageOData(cid, aid, oDataConfigObj),
      [oDataConfigObj, aid, cid]
    );

    const imageBulkEditODataService = useMemo(
      () => getImageBulkEditODataService({
        odata, oDataConfig: oDataConfigObj, aid, cid, logger,
      }),
      [logger, oDataConfigObj, aid, cid]
    );

    const folderODataService = useMemo(
      () => getFolderODataService({
        odata,
        legacyOdata,
        oDataConfig: oDataConfigObj,
        aid,
        cid,
        i18n,
        dateFormat,
      }),
      [aid, cid, dateFormat, i18n, oDataConfigObj]
    );

    const aigcODataService = useMemo(() => getAIGCODataService({
      odata, oDataConfig: oDataConfigObj, aid, cid,
    }), [aid, cid, oDataConfigObj]);

    const videoODataService = useMemo(() => getVideoODataService({
      odata, oDataConfig: oDataConfigObj, aid, cid,
    }), [aid, cid, oDataConfigObj]);

    const getODataErrors = useCallback((errs, defaultMessage) => {
      const errors = getODataErrorsImpl(errs, i18n, activity);
      if (!errors || _.isEmpty(errors)) {
        return defaultMessage;
      }
      return errors;
    }, [activity, i18n]);

    const repository = useMemo(
      () => getRepository({
        imageBulkEditODataService,
        imageODataService,
        i18n,
        ianaTimeZone,
        timezoneUTCOffset,
        getODataErrors,
      }),
      [
        imageBulkEditODataService,
        imageODataService,
        i18n,
        ianaTimeZone,
        timezoneUTCOffset,
        getODataErrors,
      ]
    );

    const azureUploader = useMemo(() => new AzureUploader({
      logger: {
        trace: logger.traceWithoutParams,
      },
    }), [logger]);

    const folderRepository = useMemo(
      () => getFolderRepository({
        azureUploader,
        folderODataService,
        i18n,
        ianaTimeZone,
        timezoneUTCOffset,
        logger,
      }),
      [azureUploader, folderODataService, i18n, ianaTimeZone, timezoneUTCOffset, logger]
    );

    const aigcRepository = useMemo(
      () => getAIGCRepository({ aigcODataService }),
      [aigcODataService]
    );

    const videoRepository = useMemo(
      () => getVideoRepository({
        videoODataService,
      }),
      [videoODataService]
    );

    const defaultPerfMetricDateRange = useMemo(() => getDefaultMetricDateRange(
      i18n, dateFormat,
      ianaTimeZone,
      perfMetricDateRange && perfMetricDateRange.Rangelcid
    ), [dateFormat, i18n, ianaTimeZone, perfMetricDateRange]);

    return (
      <AssetWithFoldersDataServiceProvider
        imageRepository={repository}
        folderRepository={folderRepository}
        aigcRepository={aigcRepository}
        videoRepository={videoRepository}
        logger={logger}
        allowedAssetType={allowedAssetType}
        i18n={i18n}
        perfMetricDateRange={perfMetricDateRange}
        defaultPerfMetricDateRange={defaultPerfMetricDateRange}
      >
        <WrappedComponent
          ianaTimeZone={ianaTimeZone}
          adsStudioUserPreferences={adsStudioUserPreferences}
          adsStudioAccountPreferences={adsStudioAccountPreferences}
          logger={logger}
          {...props}
        />
      </AssetWithFoldersDataServiceProvider>
    );
  };

  Wrapper.displayName = `withAdsStudioServicesProvider(${getDisplayName(WrappedComponent)})`;
  hoistNonReactStatics(Wrapper, WrappedComponent);

  return Wrapper;
}

export function withAdsStudioServicesLoaded(WrappedComponent, options) {
  const Wrapper = withAdsStudioServicesProvider(withAdsStudioServicesConsumer(WrappedComponent), options);

  Wrapper.displayName = `withAdsStudioServicesLoaded(${getDisplayName(WrappedComponent)})`;
  hoistNonReactStatics(Wrapper, WrappedComponent);

  return Wrapper;
}
